<script lang="ts">
  import { api, ApiError } from '$shared/api';
  import PasswordInput from '$shared/PasswordInput.svelte';
  import { getUser } from '$lib/auth.svelte';

  const user = $derived(getUser());

  let currentPassword = $state('');
  let newPassword = $state('');
  let confirmPassword = $state('');
  let pwSaving = $state(false);
  let pwMessage = $state('');
  let pwType = $state<'success' | 'error'>('success');

  let newEmail = $state('');
  let emailPassword = $state('');
  let emSaving = $state(false);
  let emMessage = $state('');
  let emType = $state<'success' | 'error'>('success');

  let delPassword = $state('');
  let delConfirm = $state(false);
  let delSaving = $state(false);
  let delMessage = $state('');
  let delType = $state<'success' | 'error'>('error');

  async function handlePassword(e: Event) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      pwMessage = 'Passwords do not match';
      pwType = 'error';
      return;
    }
    if (newPassword.length < 8) {
      pwMessage = 'Min 8 characters';
      pwType = 'error';
      return;
    }
    pwSaving = true;
    pwMessage = '';
    try {
      await api.put('/users/current/password', { current_password: currentPassword, new_password: newPassword });
      pwMessage = 'Password updated';
      pwType = 'success';
      currentPassword = newPassword = confirmPassword = '';
    } catch (err) {
      pwMessage = err instanceof ApiError ? err.message : 'An error occurred';
      pwType = 'error';
    } finally {
      pwSaving = false;
    }
  }

  async function handleEmail(e: Event) {
    e.preventDefault();
    emSaving = true;
    emMessage = '';
    try {
      await api.put('/users/current/email', { new_email: newEmail.trim(), password: emailPassword });
      emMessage = 'Verification email sent';
      emType = 'success';
      newEmail = emailPassword = '';
    } catch (err) {
      emMessage = err instanceof ApiError ? err.message : 'An error occurred';
      emType = 'error';
    } finally {
      emSaving = false;
    }
  }

  async function handleDeleteAccount(e: Event) {
    e.preventDefault();
    if (!delConfirm) {
      delMessage = 'Please check the confirmation box';
      delType = 'error';
      return;
    }
    delSaving = true;
    delMessage = '';
    try {
      await api.post('/users/current/delete-account', { password: delPassword });
      // Clear auth and redirect to login
      localStorage.removeItem('beatduel_token');
      localStorage.removeItem('beatduel_user');
      window.location.hash = '#/login';
      window.location.reload();
    } catch (err) {
      delMessage = err instanceof ApiError ? err.message : 'An error occurred';
      delType = 'error';
      delSaving = false;
    }
  }
</script>

<div class="narrow">
  <h1>Security</h1>

  <section class="card card--no-hover section">
    <h2>Change Password</h2>
    {#if pwMessage}<p class={pwType === 'error' ? 'msg--error' : 'msg--success'}>{pwMessage}</p>{/if}
    <form onsubmit={handlePassword}>
      <PasswordInput
        id="current-password"
        bind:value={currentPassword}
        placeholder="Current password"
        ariaLabel="Current password"
        required
        autocomplete="current-password"
      />
      <PasswordInput
        id="new-password"
        bind:value={newPassword}
        placeholder="New password (min 8)"
        ariaLabel="New password"
        required
        minlength={8}
        autocomplete="new-password"
      />
      <PasswordInput
        id="confirm-password"
        bind:value={confirmPassword}
        placeholder="Confirm new password"
        ariaLabel="Confirm new password"
        required
        autocomplete="new-password"
      />
      <button class="btn btn-primary" type="submit" disabled={pwSaving}
        >{pwSaving ? 'Updating...' : 'Update Password'}</button
      >
    </form>
  </section>

  <section class="card card--no-hover section">
    <h2>Change Email</h2>
    <p class="current-email">Current: {user?.email}</p>
    {#if emMessage}<p class={emType === 'error' ? 'msg--error' : 'msg--success'}>{emMessage}</p>{/if}
    <form onsubmit={handleEmail}>
      <input
        class="input"
        type="email"
        bind:value={newEmail}
        placeholder="New email"
        aria-label="New email address"
        required
      />
      <PasswordInput
        id="email-change-password"
        bind:value={emailPassword}
        placeholder="Current password"
        ariaLabel="Current password for email change"
        required
        autocomplete="current-password"
      />
      <button class="btn btn-primary" type="submit" disabled={emSaving}
        >{emSaving ? 'Sending...' : 'Update Email'}</button
      >
    </form>
  </section>

  <section class="card card--no-hover section section--danger">
    <h2>Delete Account</h2>
    <p class="danger-warning">
      This will permanently delete your account. Your tracks, votes, and profile will be removed. This action cannot be
      undone.
    </p>
    {#if delMessage}<p class={delType === 'error' ? 'msg--error' : 'msg--success'}>{delMessage}</p>{/if}
    <form onsubmit={handleDeleteAccount}>
      <PasswordInput
        id="delete-account-password"
        bind:value={delPassword}
        placeholder="Enter your password to confirm"
        ariaLabel="Password to confirm account deletion"
        required
        autocomplete="current-password"
      />
      <label class="checkbox-row">
        <input type="checkbox" bind:checked={delConfirm} />
        <span>I understand this is permanent and want to delete my account</span>
      </label>
      <button class="btn btn-danger" type="submit" disabled={delSaving || !delConfirm}
        >{delSaving ? 'Deleting...' : 'Delete My Account'}</button
      >
    </form>
  </section>
</div>

<style>
  .narrow {
    max-width: 40rem;
    margin: 0 auto;
  }
  h1 {
    margin-bottom: 1.5rem;
  }
  h2 {
    margin: 0 0 0.75rem;
    font-size: 1rem;
  }
  .section {
    padding: 1rem;
    margin-bottom: 1.5rem;
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .current-email {
    font-size: 0.875rem;
    color: var(--text-secondary);
    margin-bottom: 0.75rem;
  }

  /* Delete account */
  .section--danger {
    border-color: rgba(255, 45, 85, 0.3);
  }
  .danger-warning {
    font-size: 0.875rem;
    color: var(--text-secondary);
    margin-bottom: 0.75rem;
  }
  .checkbox-row {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    font-size: 0.875rem;
    margin-bottom: 0.75rem;
    cursor: pointer;
    color: var(--text-secondary);
  }
  .checkbox-row input[type='checkbox'] {
    margin-top: 0.2rem;
  }
  .btn-danger {
    background: var(--pink);
    border-color: var(--pink);
    color: #fff;
  }
  .btn-danger:hover:not(:disabled) {
    opacity: 0.9;
  }
  .btn-danger:disabled {
    opacity: 0.5;
  }
</style>
